import { useMutation } from '@tanstack/react-query';
import { apiFetch } from './client.js';
import type { NewStory } from './types.js';

export interface DebugAnswer {
  answer: string;
  model: string;
  usage: { inputTokens: number; outputTokens: number };
  /** Which build's transcript the assistant was given, if any (per-project only). */
  usedBuildId?: string | null;
  /** How many AWS tool calls the assistant made while answering. */
  toolCalls?: number;
}

/** Asks the assistant about a single project (optionally a specific build). */
export function useAskDebug() {
  return useMutation({
    mutationFn: (input: { projectId: string; buildId?: string; prompt: string }) =>
      apiFetch<DebugAnswer>('/admin/debug', { method: 'POST', body: input }),
  });
}

/** Asks the assistant a cross-project question (no project selected). */
export function useAskGlobalDebug() {
  return useMutation({
    mutationFn: (prompt: string) =>
      apiFetch<DebugAnswer>('/admin/debug/global', { method: 'POST', body: { prompt } }),
  });
}

export interface DraftedStory {
  story: NewStory;
  model: string;
  usage: { inputTokens: number; outputTokens: number };
}

/**
 * Drafts a user story from a free-text request (e.g. a bug), using the project's
 * debug snapshot as context. Returns a draft to review — it is not persisted
 * until the caller creates it via useCreateStory.
 */
export function useDraftStory(projectId: string) {
  return useMutation({
    mutationFn: (input: { prompt: string; buildId?: string }) =>
      apiFetch<DraftedStory>(`/projects/${projectId}/stories/draft`, {
        method: 'POST',
        body: input,
      }),
  });
}
